import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../constants/providers/AuthProvider';
import { useSidebar } from '../../context/SidebarContext';
import { NotificationProvider, useNotificationCenter } from '../../context/NotificationContext';
import { MobileAppBanner } from '../ui/MobileAppBanner';
import { UserNavHost } from '../nav/UserNavHost';
import { TopBar } from './TopBar';
import { PageLayout } from './PageLayout';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { hasIncompleteOnboarding, getOnboardingUrl, getCurrentOnboardingSection } from '../../utils/onboardingCheck';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
  /**
   * Whether to wrap content in PageLayout
   * @default true
   */
  usePageLayout?: boolean;
  /**
   * PageLayout maxWidth prop (only used if usePageLayout is true)
   * @default '7xl'
   */
  pageMaxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  /**
   * PageLayout padding prop (only used if usePageLayout is true)
   * @default 'md'
   */
  pagePadding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export function AuthenticatedLayoutContent({ 
  children,
  usePageLayout = true,
  pageMaxWidth = '7xl',
  pagePadding = 'md',
}: AuthenticatedLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, toggle: toggleSidebar } = useSidebar();
  const { session, loading: authLoading } = useAuth();
  const { isOpen: isNotificationOpen, closeNotificationCenter } = useNotificationCenter();

  // Handle onboarding redirects
  // Auth loading is handled by AuthenticatedLayoutWrapper, so we don't need a local loading state
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading || !session) {
      return;
    }

    // Check for incomplete onboarding state
    // Skip this check if we're already on an onboarding page to prevent redirect loops
    const isOnboardingPage = location.pathname.startsWith('/onboarding');
    
    // Allow access to subscriptions/discount-codes pages during subscriptions onboarding step
    const isSubscriptionRelatedPage = ['/subscriptions-plans', '/subscriptions-plans/rollops', '/subscriptions-plans/gym', '/subscriptions', '/discount-codes', '/billing/discount-codes'].includes(location.pathname) || location.pathname.startsWith('/subscriptions-plans');
    const currentOnboardingSection = getCurrentOnboardingSection();
    const isInSubscriptionsStep = currentOnboardingSection === 'subscriptions';
    
    if (!isOnboardingPage && !(isSubscriptionRelatedPage && isInSubscriptionsStep) && hasIncompleteOnboarding()) {
      const onboardingUrl = getOnboardingUrl();
      navigate(onboardingUrl);
      return;
    }
  }, [session, authLoading, navigate, location.pathname]);

  // Auth loading is handled by AuthenticatedLayoutWrapper - don't show another spinner here
  // This prevents the flash of loading state on every page navigation
  if (authLoading) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex">
      {/* Top Bar - Mobile/Tablet only (hidden on lg and up) */}
      <div className="lg:hidden">
        <TopBar />
      </div>
      
      {/* Nav Host - Desktop only (lg and up) */}
      <div className="hidden lg:block">
        <UserNavHost />
      </div>

      {/* Backdrop overlay for mobile/tablet sidebar */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Mobile/Tablet Nav Host - shown when open */}
      <div className="lg:hidden">
        <UserNavHost />
      </div>
      
      {/* Main Content Area */}
      <div 
        className={`
          flex flex-col transition-all duration-300 ease-in-out
          ${isOpen ? 'lg:ml-64 lg:w-[calc(100vw-16rem)] lg:max-w-[calc(100vw-16rem)]' : 'lg:ml-16 lg:w-[calc(100vw-4rem)] lg:max-w-[calc(100vw-4rem)]'} 
          ml-0 w-full
          min-h-screen
          pt-14 md:pt-0
        `}
      >
        {/* Page Content */}
        <main className="flex-1 overflow-auto pb-20 lg:pb-0 w-full min-w-0">
          {usePageLayout ? (
            <PageLayout maxWidth={pageMaxWidth} padding={pagePadding}>
              {children}
            </PageLayout>
          ) : (
            <div className="w-full max-w-full min-w-0">
              {children}
            </div>
          )}
        </main>
        
        {/* Mobile App Banner */}
        <MobileAppBanner />
      </div>

      {/* Notification Center Modal - Rendered at layout level to escape nav constraints */}
      <NotificationCenter
        isOpen={isNotificationOpen}
        onClose={closeNotificationCenter}
      />
    </div>
  );
}

export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = (props) => {
  return (
    <NotificationProvider>
      <AuthenticatedLayoutContent {...props} />
    </NotificationProvider>
  );
};
